import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { WireTransferSummary } from '../../../../../components/FlowOfFunds/WireTransferSummary/WireTransferSummary';
import { WireInstructionsModal } from '../../../../../components/FlowOfFunds/WireInstructionsModal/WireInstructionsModal';
import { WireInstructions } from '../../../../../models/FlowOfFunds/wire-instructions.model';
import { WireTransferGroup, UploadedDocument } from '../../../../../models/FlowOfFunds/flow-of-funds.model';
import { ConfirmWireSent } from '../../../../../slices/marketplace/flow-of-funds/thunks/confirm-wire-sent.thunk';
import { UpdateVetValueFeeWireInstructions } from '../../../../../slices/marketplace/flow-of-funds/thunks/update-vetvalue-fee-wire-instructions.thunk';
import { ConfirmWireReceipt } from '../../../../../slices/marketplace/flow-of-funds/thunks/confirm-wire-receipt.thunk';
import { UploadSenderEvidence } from '../../../../../slices/marketplace/flow-of-funds/thunks/upload-sender-evidence.thunk';
import { RemoveSenderEvidence } from '../../../../../slices/marketplace/flow-of-funds/thunks/remove-sender-evidence.thunk';
import { UploadReceiptEvidence } from '../../../../../slices/marketplace/flow-of-funds/thunks/upload-receipt-evidence.thunk';
import { RemoveReceiptEvidence } from '../../../../../slices/marketplace/flow-of-funds/thunks/remove-receipt-evidence.thunk';
import { GetWireTransferDocumentUrl } from '../../../../../slices/marketplace/flow-of-funds/thunks/get-wire-transfer-document-url.thunk';
import { wireGroupKey } from '../../../../../utils/flowOfFundsFormat';
import createNotification from '../../../../../utils/createNotification';
import { useFlowOfFunds } from '../useFlowOfFunds';

const MAX_EVIDENCE_FILE_SIZE_MB = 500;
const MAX_EVIDENCE_FILE_SIZE = MAX_EVIDENCE_FILE_SIZE_MB * 1024 * 1024;

export interface WireTransferSummarySubTabProps {
    engagementId: number;
}

export const WireTransferSummarySubTab = (props: WireTransferSummarySubTabProps) => {
    const dispatch = useDispatch() as any;
    const { flowOfFunds, isLoading } = useFlowOfFunds(props.engagementId);
    const [instructionsGroupKey, setInstructionsGroupKey] = useState<string | null>(null);
    const [pendingKey, setPendingKey] = useState<string | null>(null);

    const handleConfirmSent = (group: WireTransferGroup, paymentDate: Date) => {
        setPendingKey(`${wireGroupKey(group)}-sent`);
        dispatch(
            ConfirmWireSent({
                engagementId: props.engagementId,
                category: group.category,
                sourceRowId: group.sourceRowId,
                paymentDate,
                confirmed: true,
            })
        )
            .unwrap()
            .catch(() => {})
            .finally(() => setPendingKey(null));
    };

    const handleUndoConfirmSent = (group: WireTransferGroup) => {
        setPendingKey(`${wireGroupKey(group)}-sent`);
        dispatch(
            ConfirmWireSent({
                engagementId: props.engagementId,
                category: group.category,
                sourceRowId: group.sourceRowId,
                paymentDate: null,
                confirmed: false,
            })
        )
            .unwrap()
            .catch(() => {})
            .finally(() => setPendingKey(null));
    };

    const handleConfirmReceipt = (group: WireTransferGroup) => {
        setPendingKey(`${wireGroupKey(group)}-receipt`);
        dispatch(
            ConfirmWireReceipt({
                engagementId: props.engagementId,
                category: group.category,
                sourceRowId: group.sourceRowId,
                confirmed: true,
            })
        )
            .unwrap()
            .catch(() => {})
            .finally(() => setPendingKey(null));
    };

    const handleUndoConfirmReceipt = (group: WireTransferGroup) => {
        setPendingKey(`${wireGroupKey(group)}-receipt`);
        dispatch(
            ConfirmWireReceipt({
                engagementId: props.engagementId,
                category: group.category,
                sourceRowId: group.sourceRowId,
                confirmed: false,
            })
        )
            .unwrap()
            .catch(() => {})
            .finally(() => setPendingKey(null));
    };

    const isFileTooLarge = (file: File) => {
        if (file.size <= MAX_EVIDENCE_FILE_SIZE) {
            return false;
        }
        createNotification(`Max file size is ${MAX_EVIDENCE_FILE_SIZE_MB} Mb.`, 'error');
        return true;
    };

    const handleUploadSenderEvidence = (group: WireTransferGroup, file: File) => {
        if (isFileTooLarge(file)) {
            return;
        }
        setPendingKey(`${wireGroupKey(group)}-sender-evidence-upload`);
        dispatch(
            UploadSenderEvidence({
                engagementId: props.engagementId,
                category: group.category,
                sourceRowId: group.sourceRowId,
                file,
            })
        )
            .unwrap()
            .catch(() => {})
            .finally(() => setPendingKey(null));
    };

    const handleRemoveSenderEvidence = (group: WireTransferGroup) => {
        setPendingKey(`${wireGroupKey(group)}-sender-evidence-remove`);
        dispatch(
            RemoveSenderEvidence({
                engagementId: props.engagementId,
                category: group.category,
                sourceRowId: group.sourceRowId,
            })
        )
            .unwrap()
            .catch(() => {})
            .finally(() => setPendingKey(null));
    };

    const handleUploadReceiptEvidence = (group: WireTransferGroup, file: File) => {
        if (isFileTooLarge(file)) {
            return;
        }
        setPendingKey(`${wireGroupKey(group)}-receipt-evidence-upload`);
        dispatch(
            UploadReceiptEvidence({
                engagementId: props.engagementId,
                category: group.category,
                sourceRowId: group.sourceRowId,
                file,
            })
        )
            .unwrap()
            .catch(() => {})
            .finally(() => setPendingKey(null));
    };

    const handleRemoveReceiptEvidence = (group: WireTransferGroup) => {
        setPendingKey(`${wireGroupKey(group)}-receipt-evidence-remove`);
        dispatch(
            RemoveReceiptEvidence({
                engagementId: props.engagementId,
                category: group.category,
                sourceRowId: group.sourceRowId,
            })
        )
            .unwrap()
            .catch(() => {})
            .finally(() => setPendingKey(null));
    };

    const handleSaveWireInstructions = async (instructions: WireInstructions) => {
        try {
            await dispatch(
                UpdateVetValueFeeWireInstructions({
                    engagementId: props.engagementId,
                    wireInstructions: instructions,
                })
            ).unwrap();
            return true;
        } catch {
            return false;
        }
    };

    const handleViewDocument = (group: WireTransferGroup, document: UploadedDocument) => {
        const documentTypeSuffix = group.senderEvidenceDocument?.id === document.id
            ? 'sender-evidence-view'
            : 'receipt-evidence-view';

        setPendingKey(`${wireGroupKey(group)}-${documentTypeSuffix}`);
        dispatch(
            GetWireTransferDocumentUrl({
                engagementId: props.engagementId,
                documentId: document.id,
            })
        )
            .unwrap()
            .then((url: string) => {
                window.open(url, '_blank');
            })
            .catch(() => {})
            .finally(() => setPendingKey(null));
    };

    if (isLoading) {
        return <div className="mt-4 text-muted">Loading…</div>;
    }
    const instructionsGroup =
        flowOfFunds?.wireTransfers.find(
            (group) => wireGroupKey(group) === instructionsGroupKey
        ) ?? null;

    if (!flowOfFunds?.wireTransfers || flowOfFunds.wireTransfers.length === 0) {
        return (
            <div className="mt-4 text-muted">
                No disbursements have been recorded for this engagement yet.
            </div>
        );
    }

    return (
        <div className="mt-4">
            <WireTransferSummary
                wireTransfers={flowOfFunds.wireTransfers}
                totalDisbursementsAtClose={flowOfFunds.totalDisbursementsAtClose}
                onViewWireInstructions={(group) => setInstructionsGroupKey(wireGroupKey(group))}
                onConfirmSent={handleConfirmSent}
                onUndoConfirmSent={handleUndoConfirmSent}
                onConfirmReceipt={handleConfirmReceipt}
                onUndoConfirmReceipt={handleUndoConfirmReceipt}
                onUploadSenderEvidence={handleUploadSenderEvidence}
                onRemoveSenderEvidence={handleRemoveSenderEvidence}
                onUploadReceiptEvidence={handleUploadReceiptEvidence}
                onRemoveReceiptEvidence={handleRemoveReceiptEvidence}
                onViewDocument={handleViewDocument}
                pendingKey={pendingKey}
                closing={flowOfFunds.closing}
            />

            <WireInstructionsModal
                isOpen={instructionsGroup != null}
                instructions={instructionsGroup?.wireInstructions ?? null}
                onClose={() => setInstructionsGroupKey(null)}
                isReadOnly={!instructionsGroup?.canEditWireInstructions}
                startInEditMode={instructionsGroup?.wireInstructions == null}
                onSave={
                    instructionsGroup?.canEditWireInstructions
                        ? handleSaveWireInstructions
                        : undefined
                }
            />
        </div>
    );
};

export default WireTransferSummarySubTab;
